import EqualizerIcon from '@mui/icons-material/Equalizer';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
    Box,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React from 'react';
import { useGetNewBookingStatsQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import { openDashboardReportExportMenu } from '../../dashboardReportTableConfig';
import { StatsBreakdown, StatsRange } from '../../../../enums/reports';
import { CustomButton } from '../../../../../layout/styledcomponents/CustomButton';
import { dashboardReportRangeOptions } from '../../dashboardPeriod';
import { useDashboardReportFilters } from '../../useDashboardReportFilters';
import NewReservationsReportGraph from './NewReservationsReportGraph';
import NewReservationsReportList from './NewReservationsReportList';
import NewReservationsBookingsList from './NewReservationsBookingsList';
import ReportTableTabs, { ReportTableTab } from '../../ReportTableTabs';
import {
    experienceBreakdownToPoints,
    NewBookingCategoryBreakdown,
    visitBreakdownToPoints,
} from '../../newBookingStatsFormat';

const breakdownOptions = [
    {
        value: NewBookingCategoryBreakdown.ExperienceType,

        label: 'Experience Type',
    },

    {
        value: NewBookingCategoryBreakdown.VisitType,

        label: 'Visit Type',
    },
];

export default function NewReservationsComponentPage() {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const [graphView, setGraphView] = React.useState(true);
    const [tableTab, setTableTab] = React.useState<ReportTableTab>('summary');
    const { range, setRange } = useDashboardReportFilters();

    const [categoryBreakdown, setCategoryBreakdown] =
        React.useState<NewBookingCategoryBreakdown>(
            NewBookingCategoryBreakdown.ExperienceType,
        );

    const { data, isLoading } = useGetNewBookingStatsQuery({
        range,

        breakdown: StatsBreakdown.All,

        includeBookings: true,
    });

    const points =
        categoryBreakdown === NewBookingCategoryBreakdown.ExperienceType
            ? experienceBreakdownToPoints(
                  data?.experienceTypeBreakdown ?? {
                      boat: 0,

                      property: 0,

                      activity: 0,

                      vehicle: 0,

                      commercial: 0,
                  },
              )
            : visitBreakdownToPoints(
                  data?.visitTypeBreakdown ?? {
                      business: 0,

                      personal: 0,

                      blocked: 0,
                  },
              );

    const categoryLabel =
        categoryBreakdown === NewBookingCategoryBreakdown.ExperienceType
            ? 'Experience Type'
            : 'Visit Type';

    return (
        <Grid sx={{ mt: 1 }}>
            <Grid
                sx={{
                    display: 'flex',

                    flexDirection: isMobile ? 'column' : 'row',

                    justifyContent: 'space-between',

                    gap: 1.5,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',

                        flexDirection: isMobile ? 'column' : 'row',

                        gap: 1.5,

                        width: isMobile ? '100%' : 'auto',
                    }}
                >
                    <FormControl size='small' sx={{ minWidth: 170 }}>
                        <InputLabel id='new-booking-range-label'>
                            Range
                        </InputLabel>

                        <Select
                            labelId='new-booking-range-label'
                            value={range}
                            label='Range'
                            onChange={(event) =>
                                setRange(event.target.value as StatsRange)
                            }
                        >
                            {dashboardReportRangeOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size='small' sx={{ minWidth: 170 }}>
                        <InputLabel id='new-booking-breakdown-label'>
                            Breakdown
                        </InputLabel>

                        <Select
                            labelId='new-booking-breakdown-label'
                            value={categoryBreakdown}
                            label='Breakdown'
                            onChange={(event) =>
                                setCategoryBreakdown(
                                    event.target
                                        .value as NewBookingCategoryBreakdown,
                                )
                            }
                        >
                            {breakdownOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {graphView ? (
                        <CustomButton
                            disableRipple
                            variant='outlined'
                            color='primary'
                            onClick={() => setGraphView(false)}
                        >
                            <FormatListBulletedIcon fontSize='small' />
                        </CustomButton>
                    ) : (
                        <>
                            <CustomButton
                                disableRipple
                                variant='outlined'
                                color='primary'
                                onClick={() => setGraphView(true)}
                            >
                                <EqualizerIcon fontSize='small' />
                            </CustomButton>
                            {tableTab === 'items' ? (
                                <CustomButton
                                    disableRipple
                                    variant='contained'
                                    color='neutral'
                                    onClick={openDashboardReportExportMenu}
                                >
                                    <FileDownloadIcon fontSize='small' />
                                </CustomButton>
                            ) : null}
                        </>
                    )}
                </Box>
            </Grid>

            <Typography sx={{ mt: 2 }} variant='h5'>
                {isLoading
                    ? 'Loading...'
                    : `Total New Reservations: ${data?.totalNewBookings ?? 0}`}
            </Typography>

            {graphView ? (
                <NewReservationsReportGraph points={points} />
            ) : (
                <ReportTableTabs
                    summaryLabel={`By ${categoryLabel}`}
                    itemsLabel='Individual Reservations'
                    onTabChange={setTableTab}
                    summaryContent={
                        <NewReservationsReportList
                            points={points}
                            categoryLabel={categoryLabel}
                        />
                    }
                    itemsContent={
                        <NewReservationsBookingsList
                            bookings={data?.bookings ?? []}
                        />
                    }
                />
            )}
        </Grid>
    );
}
